/**
 * Share loading, validation, and decryption orchestration.
 *
 * Consolidates duplicated business logic from useEmbedShare, useShareLoading,
 * useShareManagement, useSidebarAccount, and useShareViewing. No React imports.
 */

import {
  getShareMeta,
  getShareBlob,
  consumeShare,
  getShareByToken,
  getShareBlobByToken,
  updateShare,
} from '@/lib/api/shares'
import {
  decryptContent,
  decryptWithApiKey,
  decodePlaintextContent,
  reencryptContent,
} from '@/lib/crypto'
import { decryptPasscodeWithApiKey } from '@/lib/crypto/aes-gcm'
import {
  getSharePasscode,
  storeSharePasscode,
  getStoredApiKey,
} from '@/lib/storage/local-storage'
import {
  getShareExtension,
  buildFullShareUrl,
} from '@/lib/utils/share-url'
import type { ShareDetails } from '@/types/share'

// ---------------------------------------------------------------------------
// Pure validation
// ---------------------------------------------------------------------------

export type ShareAccessResult =
  | { valid: false; reason: 'expired' | 'consumed' }
  | { valid: true; needsBurnConfirm: boolean }

/**
 * Validate whether a share is accessible based on its metadata.
 */
export function validateShareAccess(meta: {
  expiresAt: number
  consumedAt: number | null
  burnAfterRead: boolean
}): ShareAccessResult {
  if (meta.expiresAt < Date.now()) {
    return { valid: false, reason: 'expired' }
  }
  if (meta.consumedAt) {
    return { valid: false, reason: 'consumed' }
  }
  return { valid: true, needsBurnConfirm: meta.burnAfterRead }
}

export type EmbedAccessResult =
  | { valid: true }
  | { valid: false; error: string }

/**
 * Validate whether a share is accessible for embedding.
 */
export function validateEmbedAccess(meta: {
  embedAllowed: boolean
  expiresAt: number
  consumedAt: number | null
}): EmbedAccessResult {
  if (!meta.embedAllowed) {
    return { valid: false, error: 'Embed access requires Pro tier' }
  }
  if (meta.expiresAt < Date.now()) {
    return { valid: false, error: 'Share expired' }
  }
  if (meta.consumedAt) {
    return { valid: false, error: 'Share consumed' }
  }
  return { valid: true }
}

/**
 * Check if share metadata has all crypto fields needed for decryption.
 */
export function hasCryptoFields(meta: {
  wrappedCek: string | null
  nonce: string | null
  kdfSalt: string | null
  kdfParams: string | null
}): boolean {
  return !!(meta.wrappedCek && meta.nonce && meta.kdfSalt && meta.kdfParams)
}

export type DecryptionPath =
  | { type: 'public' }
  | { type: 'api-key'; apiKey: string }
  | { type: 'passcode'; passcode: string }
  | { type: 'needs-passcode' }

/**
 * Determine which decryption strategy to use for a share.
 */
export function determineDecryptionPath(
  meta: {
    cipher: string
    wrappedCekApiKey?: string | null
    kdfSaltApiKey?: string | null
  },
  storedApiKey: string | null,
  fragmentPasscode: string | null,
): DecryptionPath {
  if (meta.cipher === 'NONE') {
    return { type: 'public' }
  }
  if (meta.wrappedCekApiKey && meta.kdfSaltApiKey && storedApiKey) {
    return { type: 'api-key', apiKey: storedApiKey }
  }
  if (fragmentPasscode) {
    return { type: 'passcode', passcode: fragmentPasscode }
  }
  return { type: 'needs-passcode' }
}

// ---------------------------------------------------------------------------
// Async orchestrators
// ---------------------------------------------------------------------------

/**
 * Resolve a passcode for a share: check localStorage first, then try decrypting
 * the wrapped passcode with the API key.
 */
export async function resolvePasscode(
  shareId: string,
  wrappedPasscode: string | null | undefined,
  apiKey: string | null,
): Promise<string | null> {
  const stored = getSharePasscode(shareId)
  if (stored) return stored

  if (wrappedPasscode && apiKey) {
    try {
      const passcode = await decryptPasscodeWithApiKey(wrappedPasscode, apiKey)
      storeSharePasscode(shareId, passcode)
      return passcode
    } catch (error) {
      console.warn('Failed to decrypt passcode:', error)
    }
  }

  return null
}

/**
 * Fetch and decrypt a share's content based on the decryption path.
 */
export async function decryptShareContent(
  shareId: string,
  meta: ShareDetails,
  path: DecryptionPath,
): Promise<string> {
  const blob = await getShareBlob(shareId)

  switch (path.type) {
    case 'public':
      return decodePlaintextContent(blob)

    case 'api-key': {
      if (!meta.wrappedCekApiKey || !meta.kdfSaltApiKey || !meta.nonce) {
        throw new Error('Missing API key crypto fields')
      }
      return decryptWithApiKey(
        blob,
        meta.wrappedCekApiKey,
        meta.nonce,
        meta.kdfSaltApiKey,
        path.apiKey,
      )
    }

    case 'passcode': {
      if (!meta.wrappedCek || !meta.nonce || !meta.kdfSalt || !meta.kdfParams) {
        throw new Error('Missing passcode crypto fields')
      }
      return decryptContent(
        blob,
        meta.wrappedCek,
        meta.nonce,
        meta.kdfSalt,
        meta.kdfParams,
        path.passcode,
      )
    }

    case 'needs-passcode':
      throw new Error('Passcode required but not provided')
  }
}

/**
 * Fire-and-forget consume call (for burn-after-read shares).
 */
export async function consumeShareQuietly(shareId: string): Promise<void> {
  try {
    await consumeShare(shareId)
  } catch {
    // Ignore consume errors
  }
}

/**
 * Load a share for the management page (by management token).
 * Resolves the passcode and builds the full share URL.
 */
export async function loadShareForManagement(
  token: string,
): Promise<{
  share: ShareDetails & { shareUrl: string }
  isRevoked: boolean
}> {
  const data = await getShareByToken(token)

  let passcode: string | null = null
  if (!data.isPublic) {
    const apiKey = getStoredApiKey()
    passcode = await resolvePasscode(data.id, data.wrappedPasscode, apiKey)
  }

  const extension = getShareExtension(null, data.format || 'md')
  const shareUrl = buildFullShareUrl(data.id, extension, passcode)

  return {
    share: { ...data, shareUrl },
    isRevoked: !!data.revokedAt,
  }
}

/**
 * Fetch and decrypt share content for editing (using management token + passcode).
 */
export async function decryptForEdit(
  token: string,
  cryptoMeta: {
    wrappedCek: string
    nonce: string
    kdfSalt: string
    kdfParams: string
  },
  passcode: string,
): Promise<string> {
  const blob = await getShareBlobByToken(token)
  return decryptContent(
    blob,
    cryptoMeta.wrappedCek,
    cryptoMeta.nonce,
    cryptoMeta.kdfSalt,
    cryptoMeta.kdfParams,
    passcode,
  )
}

/**
 * Re-encrypt content and update a share (management flow).
 * Returns the new nonce and server-side updatedAt timestamp.
 */
export async function reencryptAndUpdate(
  token: string,
  content: string,
  cryptoMeta: {
    wrappedCek: string
    kdfSalt: string
    kdfParams: string
  },
  passcode: string,
): Promise<{ updatedAt: number; nonce: string }> {
  const { blob, nonce } = await reencryptContent(
    content,
    cryptoMeta.wrappedCek,
    cryptoMeta.kdfSalt,
    cryptoMeta.kdfParams,
    passcode,
  )

  const rawSize = new Blob([content]).size
  const encryptedSize = new Blob([blob]).size

  const result = await updateShare(token, {
    encryptedBlob: blob,
    nonce,
    rawSize,
    encryptedSize,
  })

  return { updatedAt: result.updatedAt, nonce }
}

/**
 * Load and decrypt a share for inline viewing in the editor.
 * Returns null if decryption isn't possible (e.g. no API key path available).
 */
export async function loadShareForViewing(
  shareId: string,
  apiKey: string,
): Promise<{
  content: string
  filename: string
  meta: ShareDetails
  fileType: 'public' | 'protected'
} | null> {
  const meta = await getShareMeta(shareId, { apiKey })

  const access = validateShareAccess(meta)
  if (!access.valid) {
    throw new Error(
      access.reason === 'expired'
        ? 'This share has expired'
        : 'This share has been consumed',
    )
  }

  const blob = await getShareBlob(shareId)

  // Try to decrypt using API key path or plaintext
  if (meta.cipher === 'NONE') {
    return {
      content: decodePlaintextContent(blob),
      filename: meta.filename || 'untitled',
      meta,
      fileType: 'public',
    }
  }

  if (meta.wrappedCekApiKey && meta.kdfSaltApiKey && meta.nonce) {
    const content = await decryptWithApiKey(
      blob,
      meta.wrappedCekApiKey,
      meta.nonce,
      meta.kdfSaltApiKey,
      apiKey,
    )
    return {
      content,
      filename: meta.filename || 'untitled',
      meta,
      fileType: 'protected',
    }
  }

  // Cannot decrypt without passcode
  return null
}

/**
 * Re-encrypt and update a share using the API key decryption path (owner flow).
 */
export async function reencryptAndUpdateViaApiKey(
  shareId: string,
  managementToken: string,
  content: string,
  apiKey: string,
): Promise<void> {
  const meta = await getShareMeta(shareId, { apiKey })

  if (!meta.wrappedCekApiKey || !meta.kdfSaltApiKey) {
    throw new Error('Cannot update: missing encryption keys')
  }

  const { reencryptWithApiKey } = await import('@/lib/crypto')
  const encrypted = await reencryptWithApiKey(
    content,
    meta.wrappedCekApiKey,
    meta.kdfSaltApiKey,
    apiKey,
  )

  const { updateShare: update } = await import('@/lib/api/shares')
  await update(managementToken, {
    encryptedBlob: encrypted.blob,
    nonce: encrypted.nonce,
    rawSize: new Blob([content]).size,
    encryptedSize: new Blob([encrypted.blob]).size,
  })
}
